import { Link } from "react-router-dom";
import { PINS, pinUrl } from "../lib/pins";

/**
 * PinStrip — decorative Pinterest gallery of the 30 branded term pins.
 * Placed at the bottom of every recipe page so any reader who lands via
 * search leaves knowing there are 400+ terms and pinnable art to share.
 *
 * Behaviour:
 *  - Horizontal snap-scroll on mobile, wrapping grid on desktop.
 *  - Each tile is a clickable link to /term/{term-slug}.
 *  - Uses browser-native lazy loading so it never blocks first paint.
 */
export default function PinStrip() {
  return (
    <section
      className="mt-12 md:mt-16 no-print"
      data-testid="pinterest-pin-strip"
      aria-label="Pinterest gallery of Bite and Banter term pins"
    >
      <div className="flex items-center justify-between mb-4 gap-4">
        <div>
          <div className="font-ui text-[10px] uppercase tracking-[0.3em] opacity-60">
            Pinnable art
          </div>
          <h3 className="font-display italic text-3xl md:text-4xl leading-none">
            From the Bite &amp; Banter Pinterest kitchen
          </h3>
        </div>
        <a
          href="https://www.pinterest.com/biteandbanter"
          target="_blank"
          rel="noopener noreferrer"
          className="pill hidden sm:inline-flex"
          data-testid="pin-strip-follow"
        >
          Follow on Pinterest →
        </a>
      </div>

      <div
        className="flex gap-3 md:gap-4 overflow-x-auto snap-x snap-mandatory pb-3
                   [scrollbar-width:thin] [-webkit-overflow-scrolling:touch]"
      >
        {PINS.map((filename) => {
          const termSlug = filename.replace(/^pin-\d+-/, "").replace(/\.jpg$/, "");
          return (
            <Link
              key={filename}
              to={`/term/${termSlug}`}
              className="shrink-0 snap-start rounded-xl overflow-hidden border border-black/15
                         hover:border-black transition-colors bg-white"
              style={{ width: "160px" }}
              data-testid={`pin-strip-tile-${termSlug}`}
              title={termSlug.replace(/-/g, " ")}
            >
              <img
                src={pinUrl(filename)}
                alt={`${termSlug.replace(/-/g, " ")} — Bite & Banter Pinterest pin`}
                loading="lazy"
                decoding="async"
                className="block w-full h-auto"
                style={{ aspectRatio: "2/3" }}
              />
            </Link>
          );
        })}
      </div>
    </section>
  );
}
